import React from 'react';
import Icon from '../../../components/AppIcon';

import Button from '../../../components/ui/Button';

const ChallengeCard = ({ challenge, userProgress, onJoin, onLeave, onViewDetails }) => {
  const isParticipating = !!userProgress;
  const progressPercentage = userProgress
    ? Math.min((userProgress?.current_value / challenge?.target_value) * 100, 100)
    : 0;

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'hard': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'strength': return 'Dumbbell';
      case 'cardio': return 'Activity';
      case 'endurance': return 'Timer';
      case 'weight_loss': return 'TrendingDown';
      case 'consistency': return 'Flame';
      default: return 'Trophy';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysRemaining = () => {
    const end = new Date(challenge?.end_date);
    const now = new Date();
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden hover:shadow-lg transition-smooth">
      {/* Header */}
      <div className="p-6 pb-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon
                name={getCategoryIcon(challenge?.category)}
                size={24}
                color="var(--color-primary)"
              />
            </div>
            <div>
              <h3 className="font-semibold text-foreground text-lg">
                {challenge?.title}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  getDifficultyColor(challenge?.difficulty)
                }`}>
                  {challenge?.difficulty?.toUpperCase()}
                </span>
                <span className="text-xs text-muted-foreground">
                  {challenge?.participants?.[0]?.count || 0} participants
                </span>
              </div>
            </div>
          </div>
        </div>

        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
          {challenge?.description}
        </p>

        {/* Challenge Details */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="flex items-center gap-2 text-sm">
            <Icon name="Target" size={16} color="var(--color-muted-foreground)" />
            <span className="text-muted-foreground">
              Goal: <span className="font-semibold text-foreground">
                {challenge?.target_value} {challenge?.metric_unit}
              </span>
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Icon name="Calendar" size={16} color="var(--color-muted-foreground)" />
            <span className="text-muted-foreground">
              {getDaysRemaining()} days left
            </span>
          </div>
        </div>

        {/* Progress Bar (if participating) */}
        {isParticipating && (
          <div className="mb-4">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-muted-foreground">Your Progress</span>
              <span className="font-semibold text-foreground">
                {userProgress?.current_value} / {challenge?.target_value}
              </span>
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
        )}

        {/* Rewards */}
        {challenge?.rewards && (
          <div className="flex items-center gap-2 p-3 bg-accent/10 rounded-lg mb-4">
            <Icon name="Award" size={18} color="var(--color-accent)" />
            <span className="text-sm font-medium text-foreground">
              {challenge?.rewards}
            </span>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="px-6 py-4 bg-muted/30 border-t border-border flex items-center gap-3">
        {isParticipating ? (
          <>
            <Button
              onClick={onViewDetails}
              variant="default"
              className="flex-1"
            >
              View Leaderboard
            </Button>
            <Button
              onClick={onLeave}
              variant="outline"
              className="flex-shrink-0"
            >
              Leave
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={onJoin}
              variant="default"
              className="flex-1"
            >
              Join Challenge
            </Button>
            <Button
              onClick={onViewDetails}
              variant="ghost"
              className="flex-shrink-0"
            >
              Details
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default ChallengeCard;